/**
 * Can 50 (steps 5-7 design §1.3): "Let Cleo use this Mac from Pocket and the web", the person's own switch, per World, on this Mac.
 * On, a conversation kept in the World can ask this Mac for what only it holds (its Cleo settings, and the folders chosen for the World), from any screen, while it
 * is awake. The Desk server asks the World with this Mac's own sign-in (server neucleos/macForToolsHttp.ts); the window holds no
 * credential. One Mac per person per World: turning this one on turns another of theirs off.
 */
import { useCallback, useEffect, useState } from "react";

import { fetchPrimary } from "~/environments/primary/fetch";
import { MAC_FOR_TOOLS_CHANGED_EVENT } from "../../neucleos/deskConversationRoute";
import { Switch } from "../ui/switch";

type MacForTools = { readonly on: boolean; readonly anotherMac: boolean };
type Read =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly state: MacForTools }
  | { readonly kind: "unavailable" }
  // The World refused this Mac for this World (the Desk server's 409 not_available, macForToolsHttp.ts refusedByWorld).
  | { readonly kind: "refused" };

export const MAC_FOR_TOOLS_TITLE = "Let Cleo use this Mac from Pocket and the web";

export function macForToolsDescription(read: Read): string {
  if (read.kind === "refused")
    return "This Mac isn't available to Cleo for this World right now, so Pocket and the web can't reach it for this World.";
  if (read.kind === "unavailable")
    return "Cleo can't read this switch right now. Check that this Mac is signed in to the World, then try again.";
  if (read.kind === "loading") return "Reading…";
  if (read.state.on)
    return "On: in conversations kept in this World, Cleo can ask this Mac for its Cleo settings, and search and read the folders chosen for this World on it, from Pocket, the web or here, while it is awake.";
  return read.state.anotherMac
    ? "Off here: another of your Macs is the one Cleo uses for this World. Turning this on uses this Mac instead."
    : "Off: Cleo does not reach this Mac from Pocket or the web for this World.";
}

const readOf = (state: MacForTools | "refused" | null): Read =>
  state === "refused" ? { kind: "refused" } : state ? { kind: "ready", state } : { kind: "unavailable" };

async function readSwitch(worldKey: string): Promise<MacForTools | "refused" | null> {
  const response = await fetchPrimary(
    `/api/neucleos/mac-for-tools?worldKey=${encodeURIComponent(worldKey)}`,
  ).catch(() => null);
  if (response?.status === 409) return "refused";
  if (!response?.ok) return null;
  const body = (await response.json().catch(() => null)) as Partial<MacForTools> | null;
  return typeof body?.on === "boolean" && typeof body.anotherMac === "boolean"
    ? { on: body.on, anotherMac: body.anotherMac }
    : null;
}

async function pressSwitch(worldKey: string, on: boolean): Promise<MacForTools | "refused" | null> {
  const response = await fetchPrimary("/api/neucleos/mac-for-tools", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ worldKey, on }),
  }).catch(() => null);
  if (response?.status === 409) return "refused";
  if (!response?.ok) return null;
  const body = (await response.json().catch(() => null)) as Partial<MacForTools> | null;
  return typeof body?.on === "boolean" && typeof body.anotherMac === "boolean"
    ? { on: body.on, anotherMac: body.anotherMac }
    : null;
}

export function MacForToolsSwitch({
  worldKey,
  worldName,
}: {
  readonly worldKey: string;
  readonly worldName: string;
}) {
  const [read, setRead] = useState<Read>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(async () => {
    setRead(readOf(await readSwitch(worldKey)));
  }, [worldKey]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const press = async (on: boolean) => {
    setBusy(true);
    try {
      setRead(readOf(await pressSwitch(worldKey, on)));
      // The Desk's + reads this switch to decide where a folder conversation starts (deskConversationRoute.ts).
      window.dispatchEvent(new Event(MAC_FOR_TOOLS_CHANGED_EVENT));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-start justify-between gap-3 pt-1" data-mac-for-tools={worldKey}>
      <div className="space-y-0.5">
        <p className="text-xs font-medium text-foreground">{MAC_FOR_TOOLS_TITLE}</p>
        <p className="text-xs text-muted-foreground">{macForToolsDescription(read)}</p>
      </div>
      <Switch
        aria-label={`${MAC_FOR_TOOLS_TITLE} for ${worldName}`}
        checked={read.kind === "ready" && read.state.on}
        disabled={busy || read.kind !== "ready"}
        onCheckedChange={(checked) => void press(checked)}
      />
    </div>
  );
}
